import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { UserFeedbackModal } from '@/components/modals/UserFeedbackModal/UserFeedbackModal';
import { DataTableCard } from '@/components/tables/DataTable/DataTableCard';
import { MiniTableRow } from '@/components/tables/MiniTable/MiniTable';
import { TextEditorProvider } from '@/components/TextEditor/contexts/TextEditor.provider';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { getMemoBraintrustLogsUrl } from '@/modules/braintrust/braintrustLogs';
import { useBraintrustLinks } from '@/modules/braintrust/useBraintrustLinks';
import { useCopyMemo } from '@/modules/memos/hooks/useCopyMemo';
import { useCopyMemoContentToClipboard } from '@/modules/memos/hooks/useCopyMemoContentToClipboard';
import { useDeleteMemo } from '@/modules/memos/hooks/useDeleteMemo';
import { useEnqueueMemoExport } from '@/modules/memos/hooks/useEnqueueMemoExport';
import { useEnqueueMemoWordExport } from '@/modules/memos/hooks/useEnqueueMemoWordExport';
import { useMemoPaths } from '@/modules/memos/hooks/useMemoPaths';
import { useMemoProcessing } from '@/modules/memos/hooks/useMemoProcessing';
import { useRegenerateMemo } from '@/modules/memos/hooks/useRegenerateMemo';
import { useTrowser } from '@/modules/navigation/trowsers/trowser.context';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';
import { AnnotatedPDFViewerProvider } from '@/modules/pdf/PDFViewer/AnnotatedPDFViewer/context/AnnotatedPDFViewer.provider';
import { formatDateToMMDDYY } from '@/utils/formatting/dates';
import { getNodes } from '@/utils/graphqlUtils';

import { EditMemoTrowser } from '../EditMemoTrowser/EditMemoTrowser';
import { MemoAboutSection } from './components/MemoAboutSection';
import { MemoContentPanel } from './components/MemoContentPanel';
import { RegenerateMemoModal } from './components/RegenerateMemoModal';
import { MemoDetailsContext } from './contexts/MemoDetails.context';
import { useMemoDetailsData } from './hooks/useMemoDetailsData';
import { useMemoNameForm } from './hooks/useMemoNameForm';
import { useMemoRating } from './hooks/useMemoRating';
import { getMemoAttributionText } from './utils/memoAttribution';

interface MemoDetailsProps {
  householdId: string;
  memoId: string;
}

export function MemoDetails({ householdId, memoId }: MemoDetailsProps) {
  const navigate = useNavigate();
  const { memoListPath, getMemoDetailsPath } = useMemoPaths(householdId);
  const canEditHouseholdData = useCanEditHouseholdData();
  const { showDiagnosticLinks } = useBraintrustLinks();
  const editMemoTrowser = useTrowser(TrowserKey.EditMemo);
  const isEditMemoTrowserOpen = editMemoTrowser.isOpen;
  const [isRegenerateMemoModalOpen, setIsRegenerateMemoModalOpen] =
    useState(false);
  const { data, loading, memo, refetch } = useMemoDetailsData(memoId);
  const { regenerateMemo, loading: regeneratingMemo } = useRegenerateMemo();
  const { deleteMemo, loading: deletingMemo } = useDeleteMemo();
  const { onExport: exportMemoToPdf } = useEnqueueMemoExport({
    householdId,
    source: 'memo details',
  });
  const { onExport: exportMemoToWord } = useEnqueueMemoWordExport({
    householdId,
    source: 'memo details',
  });
  const copyMemoContentToClipboard = useCopyMemoContentToClipboard();
  const { copyMemo, loading: duplicatingMemo } = useCopyMemo({
    onSuccess: (newMemoId) => {
      navigate(getMemoDetailsPath(newMemoId));
    },
  });
  const { isGenerating, onStartGenerating } = useMemoProcessing(
    memoId,
    householdId,
    refetch
  );
  const { formMethods, onSubmit, isDirty, savingMemoName } = useMemoNameForm({
    memo,
    canEditHouseholdData,
  });
  const {
    isFeedbackModalOpen,
    closeFeedbackModal,
    handleRateMemo,
    provideMemoFeedback,
    savingMemoRating,
  } = useMemoRating({
    memoId,
    hasExistingFeedback: Boolean(memo?.aiFeedback),
  });
  const disableMemoActions = deletingMemo || duplicatingMemo;
  const openEditMemoTrowser = editMemoTrowser.open;
  const handleEditMemo = useCallback(() => {
    openEditMemoTrowser();
  }, [openEditMemoTrowser]);

  const handleCloseEditMemoTrowser = editMemoTrowser.close;
  const memoDetailsContextValue = useMemo(
    () => ({
      isEditMemoTrowserOpen,
      openEditMemoTrowser: handleEditMemo,
    }),
    [handleEditMemo, isEditMemoTrowserOpen]
  );

  if (loading && !data) {
    return (
      <DataTableCard>
        <Stack py={6} alignItems="center" justifyContent="center">
          <CircularProgress size={24} />
        </Stack>
      </DataTableCard>
    );
  }

  if (!memo) {
    return (
      <DataTableCard>
        <Stack spacing={1}>
          <Typography variant="title-s">Memo not found</Typography>
          <Typography variant="body-xs">
            The memo may have been deleted or is unavailable.
          </Typography>
        </Stack>
      </DataTableCard>
    );
  }

  const aboutRows: MiniTableRow[] = [
    {
      label: 'Last edited',
      value: formatDateToMMDDYY(memo.updatedAt),
      valueProps: {
        textAlign: 'right',
      },
    },
    {
      label: 'Created by',
      value: memo.user.displayName,
      valueProps: {
        textAlign: 'right',
      },
    },
  ];

  const memoAttributionText = getMemoAttributionText({
    createdAt: memo.createdAt,
    updatedAt: memo.updatedAt,
    entryMode: memo.entryMode,
    updateUserDisplayName: memo.updateUser.displayName,
  });

  const handleOpenRegenerateMemoModal = () => {
    setIsRegenerateMemoModalOpen(true);
  };

  const handleCloseRegenerateMemoModal = () => {
    setIsRegenerateMemoModalOpen(false);
  };

  const handleRegenerateMemo = async (specificInstructions?: string) => {
    await regenerateMemo({
      memoId,
      specificInstructions,
    });
    onStartGenerating();
  };

  const handleMemoDeleted = () => {
    editMemoTrowser.close();
    navigate(memoListPath);
  };

  const handleExportMemoToPdf = () => {
    void exportMemoToPdf(memoId);
  };

  const handleExportMemoToWord = () => {
    void exportMemoToWord(memoId);
  };

  const handleCopyMemoToClipboard = () => {
    copyMemoContentToClipboard(memo.content);
  };

  const handleDuplicateMemo = () => {
    void copyMemo({ householdId, memoId });
  };

  const handleDeleteMemo = async () => {
    const deleted = await deleteMemo(memoId);
    if (deleted) {
      handleMemoDeleted();
    }
  };

  const braintrustLogsUrl = showDiagnosticLinks
    ? getMemoBraintrustLogsUrl({
        householdId,
        memoId,
      })
    : undefined;
  const initialMemoDocumentId = getNodes(memo.documents)[0]?.id ?? null;

  return (
    <AnnotatedPDFViewerProvider
      documentId={initialMemoDocumentId}
      showCitations
      showCopyCitationsButton
    >
      <MemoDetailsContext.Provider value={memoDetailsContextValue}>
        {isRegenerateMemoModalOpen && (
          <RegenerateMemoModal
            isOpen
            onClose={handleCloseRegenerateMemoModal}
            onSubmit={handleRegenerateMemo}
            loading={regeneratingMemo}
          />
        )}
        <UserFeedbackModal
          isFeedbackModalOpen={isFeedbackModalOpen}
          closeFeedbackModal={closeFeedbackModal}
          provideFeedback={provideMemoFeedback}
          headingText="Provide feedback"
          placeholderText="How could this memo be improved?"
          helpText="This memo was generated using Luminary AI. We are continuously improving our memo capabilities based on feedback."
        />
        {isEditMemoTrowserOpen && (
          <EditMemoTrowser
            householdId={householdId}
            memoId={memoId}
            onClose={handleCloseEditMemoTrowser}
            onDeleted={handleMemoDeleted}
          />
        )}
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
          <Box width={{ xs: '100%', lg: 420 }} flexShrink={0}>
            <MemoAboutSection
              formMethods={formMethods}
              onSubmit={onSubmit}
              isDirty={isDirty}
              savingMemoName={savingMemoName}
              canEditHouseholdData={canEditHouseholdData}
              aboutRows={aboutRows}
            />
          </Box>
          <Box flex={1} minWidth={0}>
            <TextEditorProvider theme="userGeneratedContent">
              <MemoContentPanel
                memo={memo}
                memoAttributionText={memoAttributionText}
                braintrustLogsUrl={braintrustLogsUrl}
                canEditHouseholdData={canEditHouseholdData}
                savingMemoRating={savingMemoRating}
                isGenerating={isGenerating}
                disableMemoActions={disableMemoActions}
                canDeleteMemo={canEditHouseholdData}
                deletingMemo={deletingMemo}
                duplicatingMemo={duplicatingMemo}
                onRateMemo={(rating) => {
                  void handleRateMemo(rating);
                }}
                onRegenerateMemo={handleOpenRegenerateMemoModal}
                onEditMemo={handleEditMemo}
                onExportMemoToPdf={handleExportMemoToPdf}
                onExportMemoToWord={handleExportMemoToWord}
                onCopyMemoToClipboard={handleCopyMemoToClipboard}
                onDuplicateMemo={handleDuplicateMemo}
                onDeleteMemo={() => {
                  void handleDeleteMemo();
                }}
              />
            </TextEditorProvider>
          </Box>
        </Stack>
      </MemoDetailsContext.Provider>
    </AnnotatedPDFViewerProvider>
  );
}
